import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X, Mail, Linkedin, Instagram, Twitter, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactForm from '../components/ContactForm';
import { portfolioProjects, services, philosophyContent, contactInfo } from '../data/mock';
// import Spline from '@splinetool/react-spline';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const sectionsRef = useRef([]);
  const containerRef = useRef(null);

  useEffect(() => {
    // Simple entrance animations without hiding content
    const loadGSAP = async () => {
      const gsap = (await import('gsap')).default;
      const ScrollTrigger = (await import('gsap/ScrollTrigger')).default;
      gsap.registerPlugin(ScrollTrigger);

      // Subtle animations that don't hide content
      gsap.utils.toArray('.reveal-text').forEach((elem, index) => {
        gsap.from(elem, {
          y: 20,
          duration: 0.6,
          delay: index * 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: elem,
            start: 'top 90%',
            once: true
          }
        });
      });

      // Portfolio cards animation
      gsap.utils.toArray('.portfolio-card').forEach((card, index) => {
        gsap.from(card, {
          y: 30,
          duration: 0.5,
          delay: index * 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            once: true
          }
        });
      });
    };

    loadGSAP();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="matrix-container" ref={containerRef}>
      {/* Fixed Navigation Header */}
      <header className="matrix-header">
        <div className="matrix-logo">
          <span className="logo-text">DESIGN MATRIX</span>
        </div>

        {/* Desktop Navigation */}
        <nav className="matrix-nav desktop-nav">
          <button onClick={() => scrollToSection('hero')} className="nav-link">HOME</button>
          <button onClick={() => scrollToSection('portfolio')} className="nav-link">PROJECTS</button>
          <button onClick={() => scrollToSection('services')} className="nav-link">SERVICES</button>
          <button onClick={() => scrollToSection('philosophy')} className="nav-link">ABOUT</button>
          <Button onClick={() => scrollToSection('contact')} className="nav-cta-btn">
            START A PROJECT
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => scrollToSection('hero')} className="mobile-nav-link">HOME</button>
            <button onClick={() => scrollToSection('portfolio')} className="mobile-nav-link">PROJECTS</button>
            <button onClick={() => scrollToSection('services')} className="mobile-nav-link">SERVICES</button>
            <button onClick={() => scrollToSection('philosophy')} className="mobile-nav-link">ABOUT</button>
            <Button onClick={() => scrollToSection('contact')} className="mobile-nav-cta">
              START A PROJECT
            </Button>
          </div>
        )}
      </header>

      {/* Hero Section with 3D Element */}
      <section
        id="hero"
        className="matrix-section hero-section"
        ref={(el) => (sectionsRef.current[0] = el)}
      >
        <div className="hero-content">
          <div className="hero-text-content">
            <h1 className="hero-headline">
              Where Your Ideas <span className="hero-highlight">Is</span> Mirrored to Reality
            </h1>
            <p className="hero-subheadline">
              From Branding to Web and Mobile Apps
            </p>
            <p className="hero-description">
              We offer solutions tailored to your business
            </p>
            <Button onClick={() => scrollToSection('contact')} className="hero-cta-btn">
              Start a Project
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>

          <div className="hero-3d-container">
            <div className="hero-3d-placeholder">
              <div className="floating-grid">
                <div className="grid-line"></div>
                <div className="grid-line"></div>
                <div className="grid-line"></div>
                <div className="floating-cube"></div>
                <div className="floating-cube"></div>
              </div>
            </div>
          </div>
        </div>

        <button onClick={() => scrollToSection('portfolio')} className="scroll-indicator">
          <ChevronDown size={32} className="scroll-icon" />
        </button>
      </section>

      {/* Portfolio Gallery Section */}
      <section
        id="portfolio"
        className="matrix-section portfolio-section"
        ref={(el) => (sectionsRef.current[1] = el)}
      >
        <div className="section-header reveal-text">
          <span className="section-label">THE GALLERY</span>
          <h2 className="section-title">Selected Works</h2>
          <p className="section-description">
            Digital masterpieces crafted for visionaries across industries
          </p>
        </div>

        <div className="portfolio-grid">
          {portfolioProjects.map((project, index) => (
            <div key={project.id} className="portfolio-card">
              <div className="portfolio-image-wrapper">
                <img
                  src={project.image}
                  alt={project.title}
                  className="portfolio-image"
                />
                <div className="portfolio-overlay">
                  <span className="portfolio-year">{project.year}</span>
                </div>
              </div>
              <div className="portfolio-content">
                <span className="portfolio-category">{project.category}</span>
                <h3 className="portfolio-title">{project.title}</h3>
                <p className="portfolio-description">{project.description}</p>
                <div className="portfolio-tags">
                  {project.tags.map((tag, i) => (
                    <span key={i} className="portfolio-tag">{tag}</span>
                  ))}
                </div>
                <div className="portfolio-impact">
                  <span className="impact-label">Impact:</span>
                  <span className="impact-value">{project.impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Philosophy Section */}
      <section
        id="philosophy"
        className="matrix-section philosophy-section"
        ref={(el) => (sectionsRef.current[2] = el)}
      >
        <div className="philosophy-content">
          <div className="section-header reveal-text">
            <span className="section-label">OUR PHILOSOPHY</span>
            <h2 className="section-title">{philosophyContent.headline}</h2>
            <p className="section-subtitle">{philosophyContent.subheadline}</p>
          </div>

          <div className="philosophy-story">
            {philosophyContent.story.map((paragraph, index) => (
              <p key={index} className="philosophy-paragraph reveal-text">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="values-grid">
            {philosophyContent.values.map((value, index) => (
              <div key={index} className="value-card reveal-text">
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="matrix-section services-section"
        ref={(el) => (sectionsRef.current[3] = el)}
      >
        <div className="section-header reveal-text">
          <span className="section-label">OUR SERVICES</span>
          <h2 className="section-title">Three Pillars of Excellence</h2>
          <p className="section-description">
            An all-in-one approach to digital transformation—from brand identity to AI-powered growth systems
          </p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <div key={service.id} className="service-card reveal-text">
              <div className="service-header">
                <h3 className="service-pillar">{service.pillar}</h3>
                <p className="service-tagline">{service.tagline}</p>
              </div>
              
              <p className="service-description">{service.description}</p>
              
              <div className="service-details-section">
                <h4 className="service-details-title">What's Included:</h4>
                <ul className="service-details-list">
                  {service.details.map((detail, i) => (
                    <li key={i} className="service-detail-item">{detail}</li>
                  ))}
                </ul>
              </div>

              <div className="service-deliverables">
                <span className="deliverables-label">Deliverables:</span>
                <p className="deliverables-text">{service.deliverables}</p>
              </div>

              {service.maintenance && (
                <div className="maintenance-badge">
                  <span className="maintenance-icon">🛡️</span>
                  <span className="maintenance-text">Includes Ongoing Maintenance & Support</span>
                </div>
              )}

              <Button onClick={() => scrollToSection('contact')} className="service-cta-btn">
                Request a Consultation
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          ))}
        </div>

        <div className="services-note reveal-text">
          <p className="note-text">
            All projects include comprehensive onboarding, training, and access to our support team. 
            We don't just deliver and disappear—we partner with you for long-term success.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="matrix-section contact-section"
        ref={(el) => (sectionsRef.current[4] = el)}
      >
        <div className="contact-content">
          <div className="contact-header reveal-text">
            <span className="section-label">THE GATEWAY</span>
            <h2 className="section-title">Let's Build Something Extraordinary</h2>
            <p className="section-description">
              Share your vision. We'll architect the solution.
            </p>
          </div>

          <div className="contact-layout">
            <div className="contact-form-wrapper reveal-text">
              <ContactForm />
            </div>

            <div className="contact-info-wrapper reveal-text">
              <div className="contact-info-card">
                <h3 className="info-title">Direct Contact</h3>
                <div className="info-items">
                  <a href={`mailto:${contactInfo.email}`} className="info-item">
                    <Mail size={20} />
                    <span>{contactInfo.email}</span>
                  </a>
                  <div className="info-item">
                    <span>{contactInfo.phone}</span>
                  </div>
                  <div className="info-item">
                    <span>{contactInfo.address}</span>
                  </div>
                </div>

                <div className="social-links">
                  <a href={contactInfo.social.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                    <Linkedin size={20} />
                  </a>
                  <a href={contactInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                    <Instagram size={20} />
                  </a>
                  <a href={contactInfo.social.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                    <Twitter size={20} />
                  </a>
                </div>
              </div>

              <div className="business-hours">
                <h4 className="hours-title">Response Time</h4>
                <p className="hours-text">We typically respond within 24 hours during business days.</p>
                <p className="hours-text">For urgent inquiries, please call directly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="matrix-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="footer-logo">DESIGN MATRIX</span>
            <p className="footer-tagline">Digital Masterpieces for Visionaries</p>
          </div>
          <div className="footer-links">
            <button onClick={() => scrollToSection('portfolio')} className="footer-link">Portfolio</button>
            <button onClick={() => scrollToSection('services')} className="footer-link">Services</button>
            <button onClick={() => scrollToSection('philosophy')} className="footer-link">Philosophy</button>
            <button onClick={() => scrollToSection('contact')} className="footer-link">Contact</button>
          </div>
          <div className="footer-legal">
            <p className="footer-copyright">© 2024 Design Matrix. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
